import React, { useState } from "react";
import { Building2, Users, FileText, ExternalLink, ChevronRight } from "lucide-react";
import { PageHero, PageBody } from "./_shared";
import { useNursing } from "./NursingLayout";

const COLORS = [
  "from-[#0F2A5F] to-[#1B3E83]",
  "from-[#0FB28A] to-[#0E916F]",
  "from-[#0FB28A] to-[#0E916F]",
  "from-[#1B3E83] to-[#0A1F47]",
  "from-[#0E916F] to-[#0A1F47]",
  "from-[#0E916F] to-[#0a7757]",
];

export default function Committees() {
  const data = useNursing();
  const list = data.committees || [];
  const [activeId, setActiveId] = useState(list[0]?.id || null);

  const activeIdx = Math.max(0, list.findIndex((c) => c.id === activeId));
  const active = list[activeIdx] || null;
  const grad = COLORS[activeIdx % COLORS.length];

  return (
    <div data-testid="nur-committees">
      <PageHero eyebrow="Governance" title="Committees &amp; Cells" />
      <PageBody>
        {list.length === 0 ? (
          <div className="text-center bg-white rounded-2xl ring-1 ring-[#E2E8F0] p-12 text-[#475569]">
            Committee details will appear here once published.
          </div>
        ) : (
          <div className="grid md:grid-cols-[280px_1fr] gap-6">
            {/* Left: list */}
            <aside
              className="bg-white rounded-2xl ring-1 ring-[#E2E8F0] md:sticky md:top-32 self-start overflow-hidden shadow-sm"
              data-testid="nur-committees-list"
            >
              <div className="px-4 py-3 border-b border-[#E2E8F0]">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#0FB28A] font-bold">Browse</div>
                <div className="font-['Poppins',sans-serif] font-bold text-lg text-[#0F2A5F]">
                  All Committees ({list.length})
                </div>
              </div>
              <ul className="max-h-[70vh] overflow-y-auto">
                {list.map((c, i) => {
                  const isActive = c.id === active?.id;
                  const itemGrad = COLORS[i % COLORS.length];
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => setActiveId(c.id)}
                        data-testid={`nur-committee-tab-${c.id}`}
                        className={`w-full text-left px-4 py-3 flex items-center gap-3 border-l-4 transition-colors ${
                          isActive ? "bg-[#E0F5EE] border-[#0FB28A]" : "border-transparent hover:bg-[#F4F6FA]"
                        }`}
                      >
                        <span className={`w-9 h-9 rounded-lg bg-gradient-to-br ${itemGrad} text-white flex items-center justify-center shrink-0 shadow-sm`}>
                          <Building2 size={14} strokeWidth={1.5} />
                        </span>
                        <span className={`flex-1 text-sm leading-snug ${isActive ? "text-[#0F2A5F] font-semibold" : "text-[#475569]"}`}>
                          {c.name}
                        </span>
                        <ChevronRight size={14} strokeWidth={1.5} className={isActive ? "text-[#0FB28A]" : "text-[#CBD5E1]"} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            {/* Right: detail */}
            {active && (
              <article
                key={active.id}
                data-testid={`nur-committee-${active.id}`}
                className="bg-white rounded-2xl overflow-hidden ring-1 ring-[#E2E8F0] shadow-sm"
              >
                <div className={`bg-gradient-to-r ${grad} p-6 text-white flex items-center gap-3 relative overflow-hidden`}>
                  <div className="absolute -top-6 -right-6 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
                  <div className="relative w-11 h-11 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                    <Building2 size={20} strokeWidth={1.5} />
                  </div>
                  <h2 className="relative font-['Poppins',sans-serif] font-bold text-xl sm:text-2xl leading-tight">
                    {active.name}
                  </h2>
                </div>
                <div className="p-6 sm:p-7">
                  {active.objective_html && (
                    <div className="mb-5">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#0FB28A] font-bold mb-1.5">Objective</div>
                      <div
                        className="prose prose-sm prose-slate max-w-none text-[#475569]"
                        dangerouslySetInnerHTML={{ __html: active.objective_html }}
                      />
                    </div>
                  )}
                  {active.has_functions && active.functions_html && (
                    <div className="mb-5">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#0FB28A] font-bold mb-1.5">Functions</div>
                      <div
                        className="prose prose-sm prose-slate max-w-none text-[#475569]"
                        dangerouslySetInnerHTML={{ __html: active.functions_html }}
                      />
                    </div>
                  )}
                  {(active.members || []).length > 0 && (
                    <div className="mt-4">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#0FB28A] font-bold mb-2 inline-flex items-center gap-1.5">
                        <Users size={11} strokeWidth={1.5} /> Members ({active.members.length})
                      </div>
                      <ul className="divide-y divide-[#E2E8F0] border border-[#E2E8F0] rounded-lg overflow-hidden">
                        {active.members.map((m, idx) => (
                          <li
                            key={idx}
                            className="px-3 py-2.5 text-sm flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 hover:bg-[#F4F6FA]"
                          >
                            <span className="font-semibold text-[#0F2A5F]">{m.name}</span>
                            {m.department && (
                              <span className="text-[#475569] text-xs">{m.department}</span>
                            )}
                            {m.designation && (
                              <span className="sm:ml-auto text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded bg-[#E0F5EE] text-[#0E916F] font-bold">
                                {m.designation}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {active.has_pdf && active.pdf_url && (
                    <a
                      href={active.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 inline-flex items-center gap-2 text-sm bg-[#E0F5EE] hover:bg-[#7FE7CE] text-[#0a7757] px-3 py-2 rounded-lg font-semibold"
                    >
                      <FileText size={14} strokeWidth={1.5} /> Open attached PDF
                      <ExternalLink size={11} strokeWidth={1.5} />
                    </a>
                  )}
                  {active.remarks && (
                    <div className="mt-5 text-xs text-[#475569] italic">{active.remarks}</div>
                  )}
                </div>
              </article>
            )}
          </div>
        )}
      </PageBody>
    </div>
  );
}
